import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useInstallPrompt } from '../../../hooks/useInstallPrompt';
import { getCountryName } from '../../../lib/countries';

// iOS PWA standalone mode doesn't scroll inputs into view when keyboard opens
function scrollInputIntoView(e: React.FocusEvent<HTMLInputElement>) {
  const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  if (isPWA) {
    setTimeout(() => {
      e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300); // Wait for keyboard animation
  }
}

const countryCode = (import.meta.env.VITE_COUNTRY_CODE as string) || '';
const countryName = getCountryName(countryCode);

function friendlyError(msg: string): string {
  const lower = msg.toLowerCase();
  if (lower.includes('failed to fetch') || lower.includes('networkerror') || lower.includes('load failed')) {
    return 'Unable to connect — please check your internet connection or try mobile data.';
  }
  if (lower.includes('invalid login') || lower.includes('invalid credentials')) {
    return 'Incorrect email or password. Please try again.';
  }
  if (lower.includes('email not confirmed')) {
    return 'Your email has not been confirmed. Check your inbox for a confirmation link.';
  }
  if (lower.includes('too many requests') || lower.includes('rate limit')) {
    return 'Too many login attempts. Please wait a moment and try again.';
  }
  return msg || 'Failed to sign in. Please try again.';
}

export function LoginPage() {
  const { signIn } = useAuth();
  const { canShowInstallPrompt, isIOS, promptInstall } = useInstallPrompt();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const result = await signIn(email, password);
    if (result.error) {
      setError(friendlyError(result.error));
    }
    setLoading(false);
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">CleanMyBed</div>
          <div className="login-subtitle">{countryName}</div>
          <div className="login-platform">Sentinel Business Management</div>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div className="form-group">
              <div className="form-error text-center">
                {error}
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label" htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              inputMode="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={scrollInputIntoView}
              placeholder="you@example.com"
              required
              disabled={loading}
              autoComplete="email"
              autoCapitalize="none"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={scrollInputIntoView}
              placeholder="Enter your password"
              required
              disabled={loading}
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary w-full mt-sm"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-xl text-center text-sm text-muted">
          <Link to="/forgot-password" className="text-primary">
            Forgot your password?
          </Link>
        </div>

        {canShowInstallPrompt && (
          <div className="login-install-prompt">
            <div className="login-install-prompt-text">
              {isIOS
                ? 'Install this app: tap Share then "Add to Home Screen"'
                : 'Install this app for quick access'}
            </div>
            {!isIOS && (
              <button
                type="button"
                className="login-install-prompt-btn"
                onClick={promptInstall}
              >
                Install App
              </button>
            )}
          </div>
        )}

        <div className="login-footer">
          &copy; {new Date().getFullYear()} Sentinel&trade; Business Management
        </div>
      </div>
    </div>
  );
}
